/* Nymoren storefront — the theme's only script. No dependencies.
 *
 * Two jobs:
 *   1. Add-to-cart without leaving the page (Buy now stays a native POST —
 *      the money path must work with JS off).
 *   2. The file-preview dialog. <dialog> gives us ESC and focus containment
 *      natively; we only page images.
 *
 * Everything re-initialises on shopify:section:load so the theme editor's
 * section re-renders keep working.
 */
(function () {
  "use strict";

  async function refreshCartCount() {
    try {
      var res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
      var cart = await res.json();
      document.querySelectorAll("[data-cart-count]").forEach(function (el) {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    } catch (e) { /* count refresh is cosmetic — never break the page */ }
  }

  function initPdpForm(root) {
    var form = root.querySelector("[data-pdp-form]");
    if (!form || form.dataset.wired) return;
    form.dataset.wired = "1";
    form.addEventListener("submit", async function (e) {
      // Buy now / free: let the server 302 to checkout.
      if (e.submitter && e.submitter.name === "return_to") return;
      e.preventDefault();
      var btn = e.submitter;
      if (btn) btn.disabled = true;
      try {
        var res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: Number(form.id.value), quantity: 1 }] }),
        });
        if (!res.ok) throw new Error("add failed");
        var added = form.parentElement.querySelector("[data-added]");
        if (added) added.hidden = false;
        await refreshCartCount();
      } catch (err) {
        form.submit(); // degrade to the native POST → /cart
        return;
      } finally {
        if (btn) btn.disabled = false;
      }
    });
  }

  function initPreview(root) {
    var pv = root.querySelector("[data-preview]");
    if (!pv || pv.dataset.wired) return;
    pv.dataset.wired = "1";
    var pages = parseInt(pv.getAttribute("data-pages"), 10) || 0;
    var handle = pv.getAttribute("data-handle");
    var dialog = pv.querySelector("[data-dialog]");
    var img = pv.querySelector("[data-page-img]");
    var counter = pv.querySelector("[data-counter]");
    if (!pages || !dialog || !img) return;

    // Asset URLs share the strip images' CDN base — derive it from the first
    // thumb so we never hardcode the cdn path or a version querystring.
    var firstThumb = pv.querySelector(".preview-strip img");
    if (!firstThumb) return;
    var base = firstThumb.src.replace(new RegExp(handle + "-1\\.jpg.*$"), "");
    var query = (firstThumb.src.match(/\.jpg(\?.*)$/) || [, ""])[1];
    var page = 1;

    function srcFor(n) { return base + handle + "-" + n + ".jpg" + query; }
    function show(n) {
      page = Math.min(Math.max(n, 1), pages);
      img.src = srcFor(page);
      img.alt = "Preview page " + page + " of " + pages;
      counter.textContent = page + " / " + pages;
      if (page < pages) new Image().src = srcFor(page + 1); // preload neighbour
    }

    pv.querySelectorAll("[data-open]").forEach(function (b) {
      b.addEventListener("click", function () {
        show(parseInt(b.getAttribute("data-open"), 10) || 1);
        dialog.showModal();
      });
    });
    dialog.querySelector("[data-prev]").addEventListener("click", function () { show(page - 1); });
    dialog.querySelector("[data-next]").addEventListener("click", function () { show(page + 1); });
    dialog.querySelector("[data-close]").addEventListener("click", function () { dialog.close(); });
    dialog.addEventListener("click", function (e) {
      if (e.target === dialog) dialog.close(); // backdrop click
    });
    dialog.addEventListener("keydown", function (e) {
      if (e.key === "ArrowLeft") show(page - 1);
      if (e.key === "ArrowRight") show(page + 1);
    });
  }

  function init(root) {
    initPdpForm(root);
    initPreview(root);
  }

  if (document.readyState !== "loading") init(document);
  else document.addEventListener("DOMContentLoaded", function () { init(document); });
  document.addEventListener("shopify:section:load", function (e) { init(e.target); });
})();

/* ---------- capture banner ------------------------------------------------
 * Visibility is NOT decided here any more. It is decided by the inline script
 * in theme.liquid's <head>, before first paint, because this file is deferred
 * and loaded after </main> and therefore cannot run before paint no matter what
 * it claims. Doing it here cost a measured 0.2491 CLS. See theme.liquid.
 *
 * What is left is the two things that genuinely happen after paint:
 *   1. Remember a dismissal, so it does not nag a returning buyer.
 *   2. Record a successful capture, so the banner does not re-ask on the next
 *      page. Shopify returns ?customer_posted=true on a successful
 *      {% form 'customer' %} — the email and the marketing consent are already
 *      stored server-side by then, so the head script has already swapped the
 *      form for the claim link and all that is missing is the flag.
 */
(function () {
  var el = document.querySelector("[data-capture]");
  if (!el) return;

  var KEY = "nymoren:capture-dismissed";
  var root = document.documentElement.classList;

  if (/[?&]customer_posted=true/.test(window.location.search)) {
    try { window.localStorage.setItem(KEY, "1"); } catch (e) { /* private mode */ }
  }

  var close = el.querySelector("[data-capture-close]");
  if (close) {
    close.addEventListener("click", function () {
      root.add("capture-off");
      try { window.localStorage.setItem(KEY, "1"); } catch (e) {}
    });
  }
})();

/* ---------- exit intent (PDP, desktop) ------------------------------------
 * When the pointer leaves the top of the viewport on a product page, the
 * capture bar gets one gentle re-surface: scroll into view plus a short
 * pulse. Desktop only (mouseleave has no mobile meaning), once per session,
 * and never for someone who has dismissed or completed the capture — their
 * no is a no. No modal: the banner is already the offer, this only points
 * at it. Guarded on matchMedia for reduced motion.
 */
(function () {
  if (!document.querySelector(".pdp")) return;
  var cap = document.querySelector("[data-capture]");
  if (!cap) return;
  var root = document.documentElement.classList;
  if (root.contains("capture-off") || root.contains("capture-posted")) return;
  var KEY = "nymoren:exit-nudged";
  try { if (window.sessionStorage.getItem(KEY) === "1") return; } catch (e) { return; }
  document.addEventListener("mouseleave", function onLeave(e) {
    if (e.clientY > 24) return;
    document.removeEventListener("mouseleave", onLeave);
    try { window.sessionStorage.setItem(KEY, "1"); } catch (err) {}
    var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    cap.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "nearest" });
    if (!reduce) {
      cap.classList.add("capture-pulse");
      setTimeout(function () { cap.classList.remove("capture-pulse"); }, 1600);
    }
  });
})();
